import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, Switch, DatePicker, message, Space } from 'antd';
import { ArtifactItemState, ArtifactProperty, ArtifactItemPropertyValue } from '../../types/artifactItems';
import { ArtifactService } from '../../services/api/artifactService';
import { StateService } from '../../services/api/stateService';
import { apiClient } from '../../services/apiClient';
import dayjs, { Dayjs } from 'dayjs';

const { TextArea } = Input;
const { Option } = Select;

interface AddArtifactItemDialogProps {
  visible: boolean;
  artifactId: string;
  onCancel: () => void;
  onSuccess: () => void;
}

interface OptionValue {
  id: string;
  value: string;
  label?: string;
}

export const AddArtifactItemDialog: React.FC<AddArtifactItemDialogProps> = ({
  visible,
  artifactId,
  onCancel,
  onSuccess,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [states, setStates] = useState<ArtifactItemState[]>([]);
  const [properties, setProperties] = useState<ArtifactProperty[]>([]);
  const [optionCatalogs, setOptionCatalogs] = useState<Map<string, OptionValue[]>>(new Map());
  const [loadingCatalogs, setLoadingCatalogs] = useState<Set<string>>(new Set());

  // Load states and properties when dialog opens
  useEffect(() => {
    if (visible && artifactId) {
      void loadData();
    } else if (!visible) {
      // Only reset fields when modal is actually closed
      try {
        form.resetFields();
      } catch (error) {
        // Ignore errors if form is not mounted
        if (process.env.NODE_ENV === 'development') {
          console.warn('[AddArtifactItemDialog] Form reset error (expected when modal is closed):', error);
        }
      }
    }
  }, [visible, artifactId, form]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Load states
      const artifactStates = await StateService.getStatesByArtifact(artifactId);
      setStates(artifactStates);

      // Load properties
      const artifactProperties = await ArtifactService.getArtifactProperties(artifactId);
      // Filter to only artifact_item scope properties
      const itemProperties = artifactProperties.filter(
        (p: ArtifactProperty) => !p.property_scope || p.property_scope === 'artifact_item'
      );
      setProperties(itemProperties);

      // Pre-load option catalogs for properties that use them
      const catalogIds = new Set<string>();
      itemProperties.forEach((prop: ArtifactProperty) => {
        if (prop.global_option_catalog_ref) {
          catalogIds.add(prop.global_option_catalog_ref);
        }
      });

      // Load all option catalogs in parallel
      const catalogPromises = Array.from(catalogIds).map(async (catalogId) => {
        if (!optionCatalogs.has(catalogId)) {
          await loadOptionCatalog(catalogId);
        }
      });

      await Promise.all(catalogPromises);
    } catch (error) {
      console.error('Error loading data for artifact item dialog:', error);
      message.error('Failed to load artifact data');
    } finally {
      setLoading(false);
    }
  };

  const loadOptionCatalog = async (catalogId: string) => {
    if (loadingCatalogs.has(catalogId) || optionCatalogs.has(catalogId)) {
      return;
    }

    try {
      setLoadingCatalogs((prev) => new Set(prev).add(catalogId));
      const response = await apiClient.get(`/option-catalogs/${catalogId}`);
      const catalog = response.data;

      if (catalog) {
        // Handle both 'options' and 'values' properties
        const catalogOptions = catalog.values || catalog.options || [];
        const options: OptionValue[] = Array.isArray(catalogOptions)
          ? catalogOptions
              .filter((opt: any) => opt.is_active !== false) // Filter out inactive options
              .map((opt: any) => ({
                id: opt.id || opt.value,
                value: opt.value || opt.id,
                label: opt.label || opt.display_label || opt.name || opt.value,
              }))
          : [];

        setOptionCatalogs((prev) => {
          const newMap = new Map(prev);
          newMap.set(catalogId, options);
          return newMap;
        });
      }
    } catch (error) {
      console.error(`Error loading option catalog ${catalogId}:`, error);
      // Set empty array on error
      setOptionCatalogs((prev) => {
        const newMap = new Map(prev);
        newMap.set(catalogId, []);
        return newMap;
      });
    } finally {
      setLoadingCatalogs((prev) => {
        const newSet = new Set(prev);
        newSet.delete(catalogId);
        return newSet;
      });
    }
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      setLoading(true);

      // Build item_data from property values
      const itemData: ArtifactItemPropertyValue = {};
      
      properties.forEach((prop) => {
        const fieldName = `property_${prop.property_key}`;
        const value = values[fieldName];
        
        if (value !== undefined && value !== null) {
          // Handle different property types
          if (prop.property_type === 'date') {
            // Check if it's a dayjs object
            if (dayjs.isDayjs(value)) {
              itemData[prop.property_key] = value.format('YYYY-MM-DD');
            } else if (typeof value === 'string') {
              itemData[prop.property_key] = value;
            }
          } else if (prop.property_type === 'boolean') {
            itemData[prop.property_key] = Boolean(value);
          } else if (prop.property_type === 'number' || prop.property_type === 'integer' || prop.property_type === 'float' || prop.property_type === 'currency' || prop.property_type === 'duration') {
            itemData[prop.property_key] = Number(value);
          } else {
            itemData[prop.property_key] = value;
          }
        } else if (prop.default_value !== undefined && prop.default_value !== null) {
          // Use default value if provided
          itemData[prop.property_key] = prop.default_value;
        }
      });

      // Create the artifact item
      await ArtifactService.createArtifactItem(
        artifactId,
        values.initial_state_id,
        itemData,
        values.item_name,
        values.description
      );

      message.success('Artifact item created successfully');
      form.resetFields();
      onSuccess();
    } catch (error: any) {
      console.error('Error creating artifact item:', error);
      if (error.errorFields) {
        // Form validation errors
        message.error('Please fix form errors');
      } else {
        message.error(error.response?.data?.detail || error.message || 'Failed to create artifact item');
      }
    } finally {
      setLoading(false);
    }
  };

  const renderPropertyField = (property: ArtifactProperty) => {
    const fieldName = `property_${property.property_key}`;
    const isRequired = property.is_required;

    // Get option catalog values if applicable
    let options: OptionValue[] = [];
    if (property.property_type === 'list' && property.global_option_catalog_ref) {
      options = optionCatalogs.get(property.global_option_catalog_ref) || [];
    }

    switch (property.property_type) {
      case 'string':
      case 'url':
        return (
          <Form.Item
            key={property.id}
            label={property.input_label || property.property_key}
            name={fieldName}
            rules={isRequired ? [{ required: true, message: `${property.property_key} is required` }] : []}
            tooltip={property.description}
          >
            <Input placeholder={property.description || `Enter ${property.property_key}`} />
          </Form.Item>
        );

      case 'number':
      case 'currency':
      case 'duration':
        return (
          <Form.Item
            key={property.id}
            label={property.input_label || property.property_key}
            name={fieldName}
            rules={isRequired ? [{ required: true, message: `${property.property_key} is required` }] : []}
            tooltip={property.description}
          >
            <Input
              type="number"
              placeholder={property.description || `Enter ${property.property_key}`}
            />
          </Form.Item>
        );

      case 'boolean':
        return (
          <Form.Item
            key={property.id}
            label={property.input_label || property.property_key}
            name={fieldName}
            valuePropName="checked"
            initialValue={
              property.default_value === 'true' || 
              property.default_value === 'True' ||
              property.default_value === '1'
            }
            tooltip={property.description}
          >
            <Switch />
          </Form.Item>
        );

      case 'date':
        return (
          <Form.Item
            key={property.id}
            label={property.input_label || property.property_key}
            name={fieldName}
            rules={isRequired ? [{ required: true, message: `${property.property_key} is required` }] : []}
            tooltip={property.description}
            getValueFromEvent={(date: Dayjs | null) => date}
            normalize={(value: string | Dayjs | null) => {
              if (!value) return undefined;
              if (typeof value === 'string') return dayjs(value);
              return value;
            }}
          >
            <DatePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
          </Form.Item>
        );

      case 'list':
        return (
          <Form.Item
            key={property.id}
            label={property.input_label || property.property_key}
            name={fieldName}
            rules={isRequired ? [{ required: true, message: `${property.property_key} is required` }] : []}
            tooltip={property.description}
          >
            <Select
              placeholder={`Select ${property.property_key}`}
              loading={loadingCatalogs.has(property.global_option_catalog_ref || '')}
            >
              {options.map((opt) => (
                <Option key={opt.id} value={opt.value}>
                  {opt.label || opt.value}
                </Option>
              ))}
            </Select>
          </Form.Item>
        );

      case 'uuid':
        // For UUID references, we'd need to load referenced artifacts
        // For now, treat as string input
        return (
          <Form.Item
            key={property.id}
            label={property.input_label || property.property_key}
            name={fieldName}
            rules={isRequired ? [{ required: true, message: `${property.property_key} is required` }] : []}
            tooltip={property.description}
          >
            <Input placeholder={`Enter ${property.property_key} (UUID)`} />
          </Form.Item>
        );

      case 'uuid[]':
        // For UUID array references, we'd need to load referenced artifacts
        // For now, treat as comma-separated string input
        return (
          <Form.Item
            key={property.id}
            label={property.input_label || property.property_key}
            name={fieldName}
            rules={isRequired ? [{ required: true, message: `${property.property_key} is required` }] : []}
            tooltip={property.description}
          >
            <Input placeholder={`Enter ${property.property_key} (comma-separated UUIDs)`} />
          </Form.Item>
        );

      default:
        return (
          <Form.Item
            key={property.id}
            label={property.input_label || property.property_key}
            name={fieldName}
            rules={isRequired ? [{ required: true, message: `${property.property_key} is required` }] : []}
            tooltip={property.description}
          >
            <Input placeholder={property.description || `Enter ${property.property_key}`} />
          </Form.Item>
        );
    }
  };

  return (
    <Modal
      title="Add Artifact Item"
      open={visible}
      onOk={handleSubmit}
      onCancel={onCancel}
      confirmLoading={loading}
      width={800}
      destroyOnHidden
    >
      <Form form={form} layout="vertical">
        <Form.Item
          label="Item Name"
          name="item_name"
          tooltip="Optional name for this artifact item"
        >
          <Input placeholder="Enter item name (optional)" />
        </Form.Item>

        <Form.Item
          label="Description"
          name="description"
          tooltip="Optional description for this artifact item"
        >
          <TextArea rows={3} placeholder="Enter description (optional)" />
        </Form.Item>

        <Form.Item
          label="Initial State"
          name="initial_state_id"
          rules={[{ required: true, message: 'Please select an initial state' }]}
          tooltip="Select the initial state for this artifact item"
        >
          <Select placeholder="Select initial state" loading={loading}>
            {states.map((state) => (
              <Option key={state.id} value={state.id}>
                {state.name}
                {state.is_initial && ' (Initial)'}
              </Option>
            ))}
          </Select>
        </Form.Item>

        {properties.length > 0 && (
          <div style={{ marginTop: 24 }}>
            <h4>Properties</h4>
            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              {properties.map((property) => renderPropertyField(property))}
            </Space>
          </div>
        )}

        {properties.length === 0 && (
          <div style={{ marginTop: 24, padding: 16, background: '#f5f5f5', borderRadius: 4 }}>
            <p style={{ margin: 0, color: '#666' }}>
              No properties defined for this artifact. Properties will be available here once they are defined.
            </p>
          </div>
        )}
      </Form>
    </Modal>
  );
};

